<script>
  import { onMount } from "svelte";
  import { fly } from "svelte/transition";

  import { translator } from "../../store";
  import {
    AdminOnly,
    Button,
    Card,
    IconButton,
    Modal,
    StarRating,
  } from "../../components/index.svelte";
  import {
  createReservationRating,
    deleteReservationRating,
    getPropertyRatings,
    updateReservationRating,
  } from "../../helpers/apiHelpers/index.svelte";
  import { PPPlaceholder, toast } from "../../helpers/utils.svelte";
  import TextInput from "../../components/Form/TextInput.svelte";
  import Textarea from "../../components/Form/Textarea.svelte";

  export let propertyID;

  let propertyRatings = [];
  let visibleRatingDetail = null;
  let ratingInputToggle;
  let ratingDeleteToggle;

  let ratingModalDetails;

  onMount(async () => {
    await initializeRatings();
  });

  function expandRating(index) {
    if (visibleRatingDetail !== index) {
      visibleRatingDetail = index;
    } else {
      visibleRatingDetail = null;
    }
  }

  export async function initializeRatings() {
    propertyRatings = await getPropertyRatings(propertyID);
  }
  export async function openRatingModal(reservationDetails) {
    if (propertyRatings && propertyRatings.length > 0) {
      ratingModalDetails = propertyRatings.filter(
        (rat) => rat.reservation_id == reservationDetails.id
      )[0];
    }
    if (!ratingModalDetails?.reservation_id) {
      ratingModalDetails = {
        reservation_id: reservationDetails.id,
        property_id: propertyID,
      };
    }
    ratingInputToggle();
  }
  function onRatingEditClick(ratingDetail) {
    ratingModalDetails = ratingDetail;
    ratingInputToggle();
  }
  function onRatingDeleteClick(ratingDetail) {
    ratingModalDetails = ratingDetail;
    ratingDeleteToggle();
  }
  async function onRatingDelete(ratingDetails) {
    try {
      await deleteReservationRating(
        ratingDetails.property_id,
        ratingDetails.reservation_id,
        ratingDetails.id
      );
      await initializeRatings();
      toast.success(
        translator("Başarılı"),
        translator(
          '"__GUESTNAME__" misafirinin değerlendirme bilgileri silindi',
          {
            __GUESTNAME__: `${ratingDetails.guest_name}`,
          }
        )
      );
      ratingModalDetails = null;
      ratingDeleteToggle();
    } catch (err) {
      toast.error(
        translator("Hata"),
        translator(
          err.getResponseMessage ? err.getResponseMessage() : err.message
        )
      );
    }
  }
  function onInputModalClose() {
    ratingModalDetails = null;
  }
  async function onReservationRatingSubmit(e) {
    const ratingData = Object.fromEntries(new FormData(e.target));
    let ratingResp;
    try {
      if (ratingModalDetails.id) {
        ratingResp = await updateReservationRating(ratingModalDetails.property_id, ratingModalDetails.reservation_id, ratingModalDetails.id, ratingData);
      } else {
        ratingResp = await createReservationRating(ratingModalDetails.property_id, ratingModalDetails.reservation_id, ratingData);
      }
      await initializeRatings();
      toast.success(
        translator("Başarılı"),
        ratingModalDetails.id
          ? translator("Değişiklikler kaydedildi")
          : translator("Değerlendirme kaydı oluşturuldu")
      );
      ratingInputToggle();
    } catch (err) {
      toast.error(translator("Hata"), translator(err.getResponseMessage ? err.getResponseMessage() : err.message));
    }
  }

  let ratingFieldValues = {
    accuracy: 0,
    communication: 0,
    cleanliness: 0,
    location: 0,
    check_in: 0,
    val: 0,
  };
  let overallValue = ratingModalDetails?.overall || 0;
  function onRatingInputChange(fieldName) {
    return (val, event) => {
      if (val > 5) {
        event.target.value = 5;
        val = 5;
      }
      ratingFieldValues[fieldName] = val;
      const fieldValues = Object.values(ratingFieldValues);
      if (fieldValues.length > 0) {
        let tempOverall = fieldValues.reduce((acc, val) => acc + parseFloat(val), 0) / fieldValues.length;
        overallValue = tempOverall.toFixed(2);
      }
    }
  } 
  $: {
    if (ratingModalDetails) {
      overallValue = ratingModalDetails?.overall || 0;
      ratingFieldValues = {
        accuracy: ratingModalDetails.accuracy || 0,
        communication: ratingModalDetails.communication || 0,
        cleanliness: ratingModalDetails.cleanliness || 0,
        location: ratingModalDetails.location || 0,
        check_in: ratingModalDetails.check_in || 0,
        val: ratingModalDetails.val || 0,
      }
    }
  }
</script>

<Card headerText={translator("Rezervasyon Değerlendirmeleri")}>
  {#if !propertyRatings || propertyRatings?.length === 0}
    <p class="text-center">Herhangi bir değerlendirme yapılmamış</p>
  {/if}
  {#each propertyRatings || [] as ratingDetail, index (index)}
    <div class="row align-items-center customer-review-list">
      <div class="col-xl-3 col-lg-4 mb-xl-0 mb-3">
        <div class="review-bx">
          <img class="me-3" src={ratingDetail?.pp_file?.url || PPPlaceholder} alt="" />
          <div>
            <h4 class="mt-1 fs-20 font-w600">
              <span class="text-black">
                {ratingDetail.guest_name}
              </span>
            </h4>
            <span class="fs-12"
              >{translator("Değerlendirme Tarihi")}
              {ratingDetail.created_at}</span
            >
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-lg-3">
        <div class="d-flex align-items-center fs-4">
          <span class="mb-1" style="margin-right: 4px;"
            >{translator("Overall:")}</span
          >
          {#key ratingDetail.overall}
            <StarRating starCount={5} fillRate={ratingDetail.overall} />
          {/key}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <i
            class:fa-caret-up={visibleRatingDetail === index}
            class:fa-caret-down={visibleRatingDetail !== index}
            class="fa mb-1 fs-2 cursor-pointer"
            style="margin-left: 4px;cursor: pointer"
            on:click={() => expandRating(index)}
          />
        </div>
        {#if visibleRatingDetail === index}
          <div
            in:fly={{ y: -100, duration: 250 }}
            out:fly={{ y: -100, duration: 250 }}
          >
            <div class="d-flex align-items-center">
              <span class="mb-1" style="margin-right: 4px;"
                >{translator("Accuracy:")}</span
              >
              <StarRating starCount={5} fillRate={ratingDetail.accuracy} />
            </div>
            <div class="d-flex align-items-center">
              <span class="mb-1" style="margin-right: 4px;"
                >{translator("Communication:")}</span
              >
              <StarRating starCount={5} fillRate={ratingDetail.communication} />
            </div>
            <div class="d-flex align-items-center">
              <span class="mb-1" style="margin-right: 4px;"
                >{translator("Cleanliness:")}</span
              >
              <StarRating starCount={5} fillRate={ratingDetail.cleanliness} />
            </div>
            <div class="d-flex align-items-center">
              <span class="mb-1" style="margin-right: 4px;"
                >{translator("Location:")}</span
              >
              <StarRating starCount={5} fillRate={ratingDetail.location} />
            </div>
            <div class="d-flex align-items-center">
              <span class="mb-1" style="margin-right: 4px;"
                >{translator("Check In:")}</span
              >
              <StarRating starCount={5} fillRate={ratingDetail.check_in} />
            </div>
            <div class="d-flex align-items-center">
              <span class="mb-1" style="margin-right: 4px;"
                >{translator("Val:")}</span
              >
              <StarRating starCount={5} fillRate={ratingDetail.val} />
            </div>
          </div>
        {/if}
      </div>
      <div class="col-xl-5 col-lg-4">
        {#if ratingDetail.guest_comment}
          <p class="mb-0">{@html ratingDetail.guest_comment?.replace(/\n/g, '<br />')}</p>
        {:else}
          <p class="mb-0" style="font-style: italic;">
            {translator("Misafir yorum yapmamış")}
          </p>
        {/if}
      </div>
      <div class="col-xl-1 col-lg-1 text-end action-btn">
        <AdminOnly>
          <IconButton
            iconCls="fa fa-edit"
            btnColorCls="btn-warning"
            onClick={() => onRatingEditClick(ratingDetail)}
          />
          <IconButton
            iconCls="fa fa-trash"
            btnColorCls="btn-red"
            onClick={() => onRatingDeleteClick(ratingDetail)}
          />
        </AdminOnly>
      </div>
    </div>
  {/each}
</Card>
<Modal
  bind:toggle={ratingInputToggle}
  title={translator("Rezervasyon Değerlendirme Bilgileri")}
  onClose={onInputModalClose}
>
  <div slot="modal-body">
    <form
      class="basic-form"
      id="rating-form"
      on:submit|preventDefault={onReservationRatingSubmit}
    >
    <div class="row">
      <div class="col-md-6 col-sm-12 p-2">
        <TextInput
          type="number"
          fieldName="accuracy"
          label="Accuracy"
          required
          step="0.01"
          min="0"
          max="5"
          onChange={onRatingInputChange('accuracy')}
          value={ratingModalDetails?.accuracy}
        />
      </div>
      <div class="col-md-6 col-sm-12 p-2">
        <TextInput
          type="number"
          fieldName="communication"
          label="Communication"
          required
          step="0.01"
          min="0"
          max="5"
          onChange={onRatingInputChange('communication')}
          value={ratingModalDetails?.communication}
        />
      </div>
      <div class="col-md-6 col-sm-12 p-2">
        <TextInput
          type="number"
          fieldName="cleanliness"
          label="Cleanliness"
          required
          step="0.01"
          min="0"
          max="5"
          onChange={onRatingInputChange('cleanliness')}
          value={ratingModalDetails?.cleanliness}
        />
      </div>
      <div class="col-md-6 col-sm-12 p-2">
        <TextInput
          type="number"
          fieldName="location"
          label="Location"
          required
          step="0.01"
          min="0"
          max="5"
          onChange={onRatingInputChange('location')}
          value={ratingModalDetails?.location}
        />
      </div>
      <div class="col-md-6 col-sm-12 p-2">
        <TextInput
          type="number"
          fieldName="val"
          label="Value"
          required
          step="0.01"
          min="0"
          max="5"
          onChange={onRatingInputChange('val')}
          value={ratingModalDetails?.val}
        />
      </div>
      <div class="col-md-6 col-sm-12 p-2">
        <TextInput
          type="number"
          fieldName="check_in"
          label="Check In"
          required
          step="0.01"
          min="0"
          max="5"
          onChange={onRatingInputChange('check_in')}
          value={ratingModalDetails?.check_in}
        />
      </div>
      <div class="col-sm-12 p-2">
        <TextInput
          type="number"
          fieldName="overall"
          label="Overall"
          disabled
          step="0.01"
          value={overallValue}
        />
      </div>
      <div class="col-sm-12 p-2">
        <Textarea
          fieldName="guest_comment"
          label="Misafir Yorumu"
          value={ratingModalDetails?.guest_comment}
        />
      </div>
    </div>
    </form>
  </div>
  <div slot="modal-footer">
    <Button
      size="sm"
      onClick={null}
      formName="rating-form"
      colorCls={ratingModalDetails?.id ? "btn-warning" : "btn-success"}
    >
      {translator(ratingModalDetails?.id ? "Güncelle" : "Kaydet")}
    </Button>
    <Button size="sm" onClick={ratingInputToggle} colorCls="btn-dark">
      {translator("Vazgeç")}
    </Button>
  </div>
</Modal>

<Modal
  bind:toggle={ratingDeleteToggle}
  title={translator("Dikkat!")}
  onClose={onInputModalClose}
>
  <div slot="modal-body">
    {translator(
      '"__GUESTNAME__" misafirinin değerlendirmesini kalıcı olarak silmek istediğinize emin misiniz?',
      {
        __GUESTNAME__: ratingModalDetails.guest_name,
      }
    )}
  </div>
  <div slot="modal-footer">
    <Button size="sm" onClick={() => onRatingDelete(ratingModalDetails)}>
      {translator("Sil")}
    </Button>
    <Button size="sm" onClick={ratingDeleteToggle} colorCls="btn-dark">
      {translator("Vazgeç")}
    </Button>
  </div>
</Modal>
